import { useMemo } from 'react';
import type { Column } from 'react-table';

type NestedColumn<TData extends object = {}> = Column<TData> & {
    columns?: Array<NestedColumn<TData>>;
};

/**
 * This hook takes columns definition, priority and returns the columns to be displayed in the table.
 *
 * @param columnsToBeIncluded List of columns to be shown in the table.
 * @param numberOfColumns Maximum number of columns to be shown in the table.
 * @param columnsConfig Columns definition.
 * @param columnsPriority Columns priority.
 * @returns Columns to be displayed in the table.
 */
export const usePrepareColumns = <
    TData extends object = {},
    TColumns extends string = string
>(
    columnsToBeIncluded: TColumns[],
    numberOfColumns: number,
    columnsConfig: Array<Column<TData>>,
    columnsPriority: TColumns[]
): Array<Column<TData>> => {
    // This variable holds the column accessors to be shown
    const filteredColumnsKeys = useMemo(
        () =>
            // Create shallow copy of the columnsToBeIncluded array
            [...columnsToBeIncluded]
                // Sort columns by priority
                .sort(
                    (colA, colB) =>
                        columnsPriority.indexOf(colA) -
                        columnsPriority.indexOf(colB)
                )
                // Slice off columns
                .slice(0, numberOfColumns)
                // Sort columns by the original order
                .sort(
                    (colA, colB) =>
                        columnsToBeIncluded.indexOf(colA) -
                        columnsToBeIncluded.indexOf(colB)
                ),
        [columnsToBeIncluded, columnsPriority, numberOfColumns]
    );

    const columns = useMemo(
        () =>
            filteredColumnsKeys
                // Map keys to column definitions
                .map((column: TColumns): Column<TData> | undefined =>
                    (columnsConfig as Array<Column<TData>>).find(
                        (configuredColumn: Column<TData>): boolean =>
                            configuredColumn.accessor === column
                    )
                )
                // Filter out undefined columns
                .filter(Boolean)
                // Check first nested level of columns
                .map((column) => {
                    // If the column has nested columns, filter them
                    if (column && (column as NestedColumn<TData>).columns) {
                        // We need to create copy, so that we don't modify the original object
                        const modifiedColumn: NestedColumn<TData> = {
                            ...column,
                        };
                        modifiedColumn.columns = (
                            column as NestedColumn<TData>
                        ).columns?.filter((col) =>
                            filteredColumnsKeys.includes(
                                col.accessor as TColumns
                            )
                        );
                        if (modifiedColumn.columns?.length === 0) {
                            return null;
                        }
                        return modifiedColumn;
                    }
                    return column;
                })
                // Filter out undefined columns
                .filter(Boolean) as Array<Column<TData>>,
        [filteredColumnsKeys, columnsConfig]
    );

    return columns;
};
